import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView } from 'framer-motion';
import { 
  ScaleIcon, 
  MicrophoneIcon, 
  DocumentTextIcon, 
  AcademicCapIcon,
  UserGroupIcon,
  ShieldCheckIcon
} from '../icons';

// Court transcription data simulation
const courtData = [
  { id: 1, court: 'Supreme Court', caseType: 'Constitutional', proceeding: 'Live Hearing', status: 'In Session', color: 'bg-court-600', x: 25, y: 20 },
  { id: 2, court: 'High Court', caseType: 'Commercial', proceeding: 'Arguments', status: 'Recording', color: 'bg-legal-700', x: 65, y: 35 },
  { id: 3, court: 'Circuit Court', caseType: 'Criminal', proceeding: 'Testimony', status: 'Transcribing', color: 'bg-accent-600', x: 80, y: 65 },
  { id: 4, court: 'District Court', caseType: 'Civil', proceeding: 'Judgment', status: 'Completed', color: 'bg-court-500', x: 30, y: 75 },
  { id: 5, court: 'Family Court', caseType: 'Family Law', proceeding: 'Mediation', status: 'In Session', color: 'bg-legal-600', x: 15, y: 50 },
  { id: 6, court: 'Special Court', caseType: 'Tax', proceeding: 'Ruling', status: 'Recording', color: 'bg-accent-500', x: 55, y: 15 },
];

const statusColors = {
  'In Session': 'border-court-500 bg-court-50',
  'Recording': 'border-red-500 bg-red-50',
  'Transcribing': 'border-accent-500 bg-accent-50',
  'Completed': 'border-green-500 bg-green-50',
};

// Court gavel icon component
const CourtIcon = ({ className }) => (
  <ScaleIcon className={className} />
);

export function CourtTranscriptionAnimation() {
  const ref = useRef(null);
  const isInView = useInView(ref);
  const [animationStage, setAnimationStage] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [transcriptionCount, setTranscriptionCount] = useState(0);
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [isRecording, setIsRecording] = useState(false);

  // Ghanaian legal phrases for realistic simulation
  const legalPhrases = [
    "Based on the evidence presented and the provisions of Act 299...",
    "The court finds that the defendant has failed to establish...",
    "Under Section 42 of the Criminal Offences Act...",
    "The prosecution has proven beyond reasonable doubt that...",
    "In accordance with the rules of natural justice...",
    "The burden of proof rests on the plaintiff to demonstrate...",
    "Pursuant to the Supreme Court Act 1993...",
    "The court is satisfied that the requirements have been met...",
    "Having considered the submissions of counsel...",
    "The principle of stare decisis applies in this matter...",
  ];

  useEffect(() => {
    const timeoutIds = [];

    if (isInView) {
      // Stage 1: Show scattered courts
      timeoutIds.push(setTimeout(() => {
        setAnimationStage(1);
      }, 500));

      // Stage 2: Start AI transcription system
      timeoutIds.push(setTimeout(() => {
        setAnimationStage(2);
        setIsRecording(true);
      }, 2000));

      // Stage 3: Show transcription status clusters
      timeoutIds.push(setTimeout(() => {
        setAnimationStage(3);
      }, 3500));

      // Stage 4: Highlight statuses cyclically
      timeoutIds.push(setTimeout(() => {
        const statuses = ['In Session', 'Recording', 'Transcribing', 'Completed'];
        let currentIndex = 0;

        const highlightInterval = setInterval(() => {
          setSelectedStatus(statuses[currentIndex]);
          currentIndex = (currentIndex + 1) % statuses.length;
        }, 1500);

        timeoutIds.push(highlightInterval);
      }, 4500));

      // Transcription counter animation
      const transcriptionInterval = setInterval(() => {
        setTranscriptionCount(prev => prev + Math.floor(Math.random() * 3) + 1);
      }, 1200);
      timeoutIds.push(transcriptionInterval);

      // Live transcript simulation
      const transcriptInterval = setInterval(() => {
        const randomPhrase = legalPhrases[Math.floor(Math.random() * legalPhrases.length)];
        setCurrentTranscript(prev => {
          if (Math.random() > 0.3) {
            return randomPhrase.substring(0, Math.floor(Math.random() * 40) + 20) + '...';
          }
          return prev;
        });
      }, 3000);
      timeoutIds.push(transcriptInterval);

    } else {
      setAnimationStage(0);
      setSelectedStatus(null);
      setTranscriptionCount(0);
      setCurrentTranscript('');
      setIsRecording(false);
    }

    return () => {
      timeoutIds.forEach(id => clearTimeout(id));
    };
  }, [isInView]);

  return (
    <div
      ref={ref}
      className="w-full h-full relative flex items-center justify-center overflow-hidden gradient-legal rounded-2xl border border-legal-200"
    >
      {/* Gradient overlays */}
      <div className="pointer-events-none absolute bottom-0 left-0 h-20 w-full bg-gradient-to-t from-white/80 to-transparent z-20"></div>
      <div className="pointer-events-none absolute top-0 left-0 h-20 w-full bg-gradient-to-b from-white/80 to-transparent z-20"></div>

      {/* Main visualization area */}
      <div className="relative w-full h-full max-w-md max-h-80 mx-auto">

        {/* Snwolley CourtAI Center */}
        <motion.div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-30"
          initial={{ scale: 0, opacity: 0 }}
          animate={{
            scale: animationStage >= 2 ? 1 : 0,
            opacity: animationStage >= 2 ? 1 : 0
          }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <div className="relative">
            <div className="size-16 gradient-court rounded-full flex items-center justify-center shadow-lg">
              <CourtIcon className="size-8 text-white" />
            </div>

            {/* AI text */}
            <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-xs font-bold text-legal-700 whitespace-nowrap">
              CourtScribe AI
            </div>

            {/* Recording indicator */}
            <AnimatePresence>
              {isRecording && (
                <motion.div
                  className="absolute -top-2 -right-2 w-3 h-3 bg-red-500 rounded-full"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  <div className="absolute inset-0 bg-red-500 rounded-full animate-ping"></div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Pulsing rings */}
            <motion.div
              className="absolute inset-0 rounded-full border-2 border-court-500/30"
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.5, 0, 0.5]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </div>
        </motion.div>

        {/* Court dots */}
        <AnimatePresence>
          {courtData.map((court, index) => (
            <motion.div
              key={court.id}
              className="absolute"
              initial={{
                left: '50%',
                top: '50%',
                x: '-50%',
                y: '-50%',
                opacity: 0,
                scale: 0
              }}
              animate={{
                left: animationStage >= 1 ? `${court.x}%` : '50%',
                top: animationStage >= 1 ? `${court.y}%` : '50%',
                opacity: animationStage >= 1 ? 1 : 0,
                scale: animationStage >= 1 ? 1 : 0,
              }}
              transition={{
                duration: 0.8,
                delay: index * 0.1,
                ease: "easeOut"
              }}
            >
              <motion.div
                className={`relative size-4 ${court.color} rounded-full shadow-md cursor-pointer border-2 border-white`}
                whileHover={{ scale: 1.2 }}
                animate={{
                  scale: selectedStatus === court.status ? 1.3 : 1,
                  boxShadow: selectedStatus === court.status
                    ? '0 0 20px rgba(245, 158, 11, 0.6)'
                    : '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
                transition={{ duration: 0.3 }}
              >
                {/* Court info tooltip */}
                <AnimatePresence>
                  {selectedStatus === court.status && (
                    <motion.div
                      className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs rounded px-2 py-1 whitespace-nowrap z-40"
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 5 }}
                    >
                      {court.court} - {court.proceeding}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Live recording indicators */}
                <AnimatePresence>
                  {animationStage >= 2 && court.status === 'Recording' && (
                    <motion.div
                      className="absolute -top-1 -right-1 size-2 bg-red-500 rounded-full border border-white"
                      initial={{ scale: 0 }}
                      animate={{ scale: [0, 1.2, 1] }}
                      transition={{ 
                        duration: 0.5, 
                        delay: Math.random() * 2,
                        repeat: Infinity,
                        repeatDelay: Math.random() * 3 + 2
                      }}
                    />
                  )}
                </AnimatePresence>
              </motion.div>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Court Status Clusters */}
        <AnimatePresence>
          {animationStage >= 3 && (
            <>
              {/* In Session */}
              <motion.div
                className={`absolute top-[10%] left-[10%] w-24 h-20 rounded-xl border-2 ${statusColors['In Session']} ${selectedStatus === 'In Session' ? 'scale-110 shadow-lg' : ''}`}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 0.7, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <div className="p-2 text-center">
                  <div className="text-xs font-bold text-court-700">In Session</div>
                  <div className="text-xs text-court-600">Live Hearings</div>
                </div>
              </motion.div>

              {/* Recording */}
              <motion.div
                className={`absolute top-[15%] right-[15%] w-24 h-20 rounded-xl border-2 ${statusColors['Recording']} ${selectedStatus === 'Recording' ? 'scale-110 shadow-lg' : ''}`}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 0.7, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <div className="p-2 text-center">
                  <div className="text-xs font-bold text-red-700">Recording</div>
                  <div className="text-xs text-red-600">Capturing Audio</div>
                </div>
              </motion.div>

              {/* Transcribing */}
              <motion.div
                className={`absolute bottom-[15%] right-[10%] w-24 h-20 rounded-xl border-2 ${statusColors['Transcribing']} ${selectedStatus === 'Transcribing' ? 'scale-110 shadow-lg' : ''}`}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 0.7, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <div className="p-2 text-center">
                  <div className="text-xs font-bold text-accent-700">Transcribing</div>
                  <div className="text-xs text-accent-600">AI Processing</div>
                </div>
              </motion.div>

              {/* Completed */}
              <motion.div
                className={`absolute bottom-[20%] left-[15%] w-24 h-20 rounded-xl border-2 ${statusColors['Completed']} ${selectedStatus === 'Completed' ? 'scale-110 shadow-lg' : ''}`}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 0.7, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <div className="p-2 text-center">
                  <div className="text-xs font-bold text-green-700">Completed</div>
                  <div className="text-xs text-green-600">Ready for Review</div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Connection lines from courts to CourtScribe AI */}
        <AnimatePresence>
          {animationStage >= 2 && courtData.map((court, index) => (
            <motion.svg
              key={`line-${court.id}`}
              className="absolute inset-0 w-full h-full pointer-events-none z-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.3 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <motion.line
                x1={`${court.x}%`}
                y1={`${court.y}%`}
                x2="50%"
                y2="50%"
                stroke="rgb(245, 158, 11)"
                strokeWidth="1"
                strokeDasharray="3,3"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1, delay: index * 0.1 }}
              />
            </motion.svg>
          ))}
        </AnimatePresence>

        {/* Live transcript display */}
        <AnimatePresence>
          {animationStage >= 2 && currentTranscript && (
            <motion.div
              className="absolute top-[25%] left-[5%] right-[5%] bg-white rounded-lg shadow-md p-3 text-xs border border-legal-200 max-w-48"
              initial={{ opacity: 0, scale: 0, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0, y: 20 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center mb-1">
                <div className="w-2 h-2 bg-red-500 rounded-full mr-2 animate-pulse"></div>
                <span className="text-xs font-bold text-legal-700">Live Transcription</span>
              </div>
              <div className="text-legal-600 italic">"{currentTranscript}"</div>
              <div className="text-xs text-court-600 mt-1">- Hon. Justice</div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Floating AI insights */}
        <AnimatePresence>
          {animationStage >= 2 && (
            <>
              <motion.div
                className="absolute top-[60%] right-[5%] bg-gradient-to-r from-accent-500 to-blue-600 text-white rounded-lg shadow-md p-2 text-xs max-w-20"
                initial={{ opacity: 0, scale: 0, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.5 }}
              >
                <div>AI Summary Ready 📋</div>
              </motion.div>

              <motion.div
                className="absolute bottom-[25%] left-[5%] bg-white rounded-lg shadow-md p-2 text-xs max-w-24 border border-legal-200"
                initial={{ opacity: 0, scale: 0, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 2 }}
              >
                <div className="text-legal-600">Legal Cited: 3 precedents found ⚖️</div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>

      {/* Bottom insights panel */}
      <AnimatePresence>
        {animationStage >= 3 && (
          <motion.div
            className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur-sm border border-legal-200 rounded-lg p-3 z-30"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-court-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-bold text-legal-700">Snwolley CourtScribe AI Active</span>
              </div>
              <div className="text-xs text-legal-600">
                {transcriptionCount.toLocaleString()}+ proceedings transcribed
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}